import axios from 'axios';

import restEndpoints from 'constants/restEndpoints';
import IProduct from 'models/product';

export type TChunkWarning = { chunk: number; totalChunks: number; error: string };

// What the server answers for an import: the products, or a code and a message.
export type TAiAnswerBody = {
  data?: { products?: Partial<IProduct>[] };
  warnings?: TChunkWarning[];
  code?: string;
  error?: string;
  issues?: unknown[];
};

export type TRunProgress = { stage: string; chunksDone: number; totalChunks: number; productCount: number };

export type TRunStatusValue = 'running' | 'success' | 'partial' | 'rejected' | 'error' | 'aborted' | 'cancelled' | 'abandoned';

// GET /api/ai/image-bulk/:runId (?summary=1 leaves answer.body out).
export type TRunStatus = {
  runId: string;
  status: TRunStatusValue;
  startedAt: string;
  cancelRequested?: boolean;
  // The browser's sha256 of the file it sent (utils/aiImportRun.ts fileSha256), and the file as the
  // server stored it (null until stored): the source file of the products saved from the import.
  file?: { name: string | null; sha256: string | null; url?: string | null };
  categoryId?: string | null;
  categoryName?: string | null;
  // Products saved from its answer ("Importera data").
  savedProducts?: number;
  savedRows?: { index: number; productId: string }[];
  // The producer its products are saved for, by the producer list's id (a superuser's import: from its
  // first save, null before).
  producer?: { id: string; name: string | null; username: string | null } | null;
  progress: TRunProgress | null;
  answer: {
    httpStatus: number | null;
    code?: string | null;
    products?: number | null;
    // The products that go to the review (not the rows the model said are no products).
    reviewable?: number | null;
    bodyTruncated: boolean;
    body?: TAiAnswerBody | null;
  } | null;
};

// The code of the answer of an import its user cancelled.
export const AI_BULK_CANCELLED_CODE = 'AI_BULK_CANCELLED';
const REQUEST_TIMEOUT_MS = 30_000;

const auth = (token?: string) => ({ Authorization: `Bearer ${token}` });

export const fetchRunStatus = async (runId: string, token: string | undefined, options: { summary?: boolean; signal?: AbortSignal } = {}) =>
  (
    await axios.get<TRunStatus>(`${restEndpoints.aiImageBulk}/${runId}${options.summary ? '?summary=1' : ''}`, {
      headers: auth(token),
      timeout: REQUEST_TIMEOUT_MS,
      signal: options.signal,
    })
  ).data;

// Asks the server to stop an import; 409 when it is no longer running.
export const requestRunCancel = (runId: string, token: string | undefined) =>
  axios.post(`${restEndpoints.aiImageBulk}/${runId}/cancel`, null, { headers: auth(token), timeout: REQUEST_TIMEOUT_MS });
